import _ from 'lodash';
import ReactGA from 'react-ga';

import environment from './constants/environment';
import locations from './constants/locations';

const countryMap = {
  uk: 'United Kingdom',
  au: 'Australia',
};

// startsWith polyfill
// Due to startsWith being an es6 method, IE11 needs a polyfill for it to work
if (!String.prototype.startsWith) {
  // eslint-disable-next-line
  String.prototype.startsWith = function(searchString, position) {
    position = position || 0;
    return this.indexOf(searchString, position) === position;
  };
}

export function newGuid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (
    s4() +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    s4() +
    s4()
  );
}

const vendorHasDiscount = type => packages => {
  const fieldName = `price_variation_${type}`;
  return !!(
    packages &&
    typeof packages === 'object' &&
    !Array.isArray(packages) &&
    Object.keys(packages).filter(key => {
      const singlePackage = packages[key];
      return !!singlePackage[fieldName];
    }).length
  );
};

const previouslyContactedKey = 'previously-contacted-vendor-messages';

export const saveToPreviouslyContactedList = ({ vendorId, formData }) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const existing =
      JSON.parse(window.localStorage.getItem(previouslyContactedKey)) || {};
    existing[vendorId] = {
      ...formData,
      date: new Date().toISOString(),
    };
    const str = JSON.stringify(existing);
    window.localStorage.setItem(previouslyContactedKey, str);
  }
};

export const capitalise = str => {
  if (!str) {
    return '';
  }
  return '' + str[0].toUpperCase() + '' + str.substr(1);
};

export const stripWhitespace = str => {
  return str.replaceAll(' ', '').trim();
};

export const generateImageFromCDN = (image, options = {}) => {
  const defaultOptions = {
    width: 800,
    height: 600,
    fit: 'crop',
    // facepad: '2.5',
  };
  if (!image) {
    return false;
  }
  const url = image.original || image;
  const o = Object.assign({}, defaultOptions, options);
  const IMGOPTIM_OPTIONS = `${o.width}x${o.height},${o.fit},quality=high`;
  if (url.indexOf('uploads.sayi.do') > -1) {
    // Imgix CDN
    // const cdnRoot = `https://sayido.imgix.net`;
    // const queryStr = `w=${o.width}&h=${o.height}&fit=${o.fit}&facepad=${o.facepad}`;
    // BUNNY CDN
    const cdnRoot = 'https://sayido.b-cdn.net/';
    const path = url.split('uploads.sayi.do')[1];
    const queryStr = Object.keys(o)
      .map(k => `${k}=${o[k]}`)
      .join('&');
    // const queryStr = `width=${o.width}&height=${o.height}`;
    return `${cdnRoot}${path}?${queryStr}`;
  } else if (url.indexOf('amazonaws.com') > -1) {
    const cdnRoot = 'https://sayido-legacy.b-cdn.net/';
    const path = url.split('amazonaws.com')[1];
    const queryStr = `width=${o.width}&height=${o.height}`;
    return `${cdnRoot}${path}?${queryStr}`;
    // } else if (url.indexOf('amazonaws.com') > -1) {
    // Image is using legacy AWS storage, so use imageoptim (assuming the free account sticks around)
    // TODO: Deprecate after 2020, remove those old vendors
    // return `https://img.gs/kzdlmwpqqk/${IMGOPTIM_OPTIONS}/${url}`;
  }
  // Image is _somehow_ using the wrong GCS bucket, so use imageoptim til we can work out how many of these there are and how to get rid of them. I suspect we had the filestack saving to the wrong bucket for a short period of time ¯\_(ツ)_/¯
  return `https://img.gs/kzdlmwpqqk/${IMGOPTIM_OPTIONS}/${url}`;
};

export const generateURLPath = ({ type, area_level_1, area_level_2, tag }) => {
  let path = ``;
  if (!type) {
    return path;
  }
  path = `${path}/${typeToSEOType(type)}`;
  if (!area_level_1) {
    return path;
  }
  path = `${path}/${area_level_1}`;
  if (!area_level_2 || area_level_2 === 'all') {
    return path;
  }
  path = `${path}/${area_level_2}`;
  if (!tag) {
    return path;
  }
  path = `${path}/find/${tag}`;
  return path;
};

export const formatLocation = (country, key = null) => {
  if (!country) {
    return null;
  }
  const l0 = locations[country];
  let location = null;
  if (!l0) {
    return null;
  }
  // If nothing is passed into the key, we want the country,
  // but if what is passed into the key is undefined,
  // then continue (will like result in an error)
  if (key === null) {
    return countryMap[country];
  }
  // eslint-disable-next-line
  l0.map(l1 => {
    if (l1.value === key) {
      return (location = l1.label);
    }
    // eslint-disable-next-line
    l1.children.map(l2 => {
      if (l2.value === key) {
        return (location = l2.label);
      }
    });
  });
  return location;
};

export const getCountryFromArea = (level1, level2) => {
  // TODO: If required, handle level 2 as well, but it currently isnt
  // as UK and Aust share no common area_level_1 names.
  return (
    Object.keys(locations).filter(countryKey => {
      const level1Options = locations[countryKey];
      const matchingLevel1 = level1Options
        .map(l1 => l1.value === level1)
        .filter(c => !!c);
      return !!matchingLevel1.length;
    })[0] || 'uk'
  );
};

export const getFirstName = contact => {
  const trimmed = contact ? contact.trim() : null;
  if (!trimmed) {
    return '';
  }
  return trimmed.split(' ')[0];
};

export const vendorHasDiscountDays = vendorHasDiscount('day');
export const vendorHasDiscountSeasons = vendorHasDiscount('season');

export const isCSR = typeof window !== 'undefined';
export const isSSR = !isCSR;

export const isMobile = isCSR && window.innerWidth < 769;

export const getCurrencySymbol = (country = 'au') => {
  return {
    au: '$',
    uk: '£',
  }[country];
};

const adminUIDs = [
  '2gNtB3djFTh4JJ5aoFOS41khvKQ2',
  'QYs2OJz2UJSVU29IarT2JCT7Spj2',
];

export const trackEvent = ({
  userId,
  vendorId,
  event,
  category = 'All',
  label = null,
}) => {
  const trackingURL = `${environment.apiURL}/vendor/event/`;
  if (environment.name === 'development') {
    console.log(`DEVELOPMENT MODE: ${event}`, {
      userId,
      vendorId,
      sessionId,
      event,
      category,
      label,
    });
    return;
  }
  const sessionId =
    (isCSR &&
      window.sessionStorage &&
      window.sessionStorage.getItem('session-id')) ||
    newGuid();
  console.log(`${event}`, { userId, vendorId, event, sessionId });
  let body = {
    vendorId,
    event,
    sessionId,
  };
  if (userId) {
    body['userId'] = userId;
  }
  if (sessionId) {
    window.sessionStorage.setItem('session-id', sessionId);
  }
  if (adminUIDs.includes(userId)) {
    return;
  }
  fetch(trackingURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    body: JSON.stringify(body),
  });
  ReactGA.event({
    action: event,
    category,
    label,
  });
};

export const SEOTypeToType = seoType => {
  const type = seoType.replace('wedding-', '');
  const shouldBePlural = type.indexOf('cars') > -1;
  return type.slice(-1) === 's' && !shouldBePlural ? type.slice(0, -1) : type;
};

export const typeToSEOType = type => {
  const len = type.length;
  const isAlreadyPlural = type[len - 1] === 's';
  return `wedding-${type}${isAlreadyPlural ? '' : 's'}`;
};

const replaceCharacter = (input, strip, replacement = '') => {
  return input.split(strip).join(replacement);
};

const pluralise = input => {
  const len = input.length;
  const isAlreadyPlural = input[len - 1] === 's';
  return `${input}${isAlreadyPlural ? '' : 's'}`;
};

export const typeToReadableSEOType = (type, makePlural = true) => {
  return `${capitalise(
    replaceCharacter(makePlural ? pluralise(type) : type, '-', ' ')
  )}`;
};
